import React, { useEffect, useRef, useState } from 'react';
import ReactDOM from 'react-dom';
import { useDispatch, useSelector } from 'react-redux';
import { useOutletContext } from 'react-router-dom';
import PerfectScrollbar from 'react-perfect-scrollbar';

import ConstituentSelect from '../ConstituentSelect/ConstituentSelect';
import InputItem from '../Forms/InputItem/InputItem';
import CustomSelect from '../Forms/CustomSelect/CustomSelect';
import FormButton from '../Forms/FormButton/FormButton';
import Fader from '../Loaders/Fader';

import {
  CASE_SOURCE_TYPES,
  FEEDBACK_TYPES,
  IMPORTANCE_TYPES,
  SOCIAL_SERVICES_SUBTYPES,
  USER_ROLES,
} from '../../../util/constants/AppConstants';
import useOutsideClick from '../../../hooks/useOutsideClick';
import { setNotify } from '../../../datastore/actions/actionActions';
import { checkForEmptyString, checkPostalCode } from '../../../util/HelperFunctions';
import { getConstituents, getCouncillorConstituents } from '../../../http/constituentAPI';
import { createCase, saveCase } from '../../../http/caseAPI';

import './CaseForm.scss';

const CaseForm = ({ isOpen, onCloseHandler, caseData, handleGetCaseData }) => {
  const dispatch = useDispatch();

  const [constituents, setConstituents] = useState([]);
  const [loadingConstituents, setLoadingConstituents] = useState(false);
  const [selectedConstituent, setSelectedConstituent] = useState(null);
  const [createMode, setCreateMode] = useState(false);

  const [source, setSource] = useState('');
  const [name, setName] = useState('');
  const [phone, setPhone] = useState('');
  const [email, setEmail] = useState('');
  const [address, setAddress] = useState('');
  const [postalCode, setPostalCode] = useState('');
  const [type, setType] = useState('');
  const [subtype, setSubtype] = useState('');
  const [importance, setImportance] = useState(IMPORTANCE_TYPES[2].value);
  const [description, setDescription] = useState('');
  const [memberNotes, setMemberNotes] = useState('');
  const [saving, setSaving] = useState(false);

  const { userInfo } = useOutletContext();

  const config = useSelector((state) => state.config.config);

  const windowRef = useRef();

  const mppRoleValue = USER_ROLES.find((role) => role.short === 'MPP').value;
  const councilorRoleValue = USER_ROLES.find((role) => role.short === 'Councillor').value;

  const isCouncillor = userInfo && userInfo.role === councilorRoleValue;

  useEffect(() => {
    if (caseData) {
      if (caseData.constituent_id) {
        setSelectedConstituent({
          id: caseData.constituent_id,
          name: caseData.name,
          phone: caseData.phone,
          email: caseData.email,
          address: caseData.address,
          postal_code: caseData.postal_code,
        });
      } else {
        setName(caseData.name);
        setPhone(caseData.phone);
        setEmail(caseData.email);
        setAddress(caseData.address);
        setPostalCode(caseData.postal_code);
      }
      setSource(caseData.case_source);
      setType(caseData.type);
      setSubtype(caseData.subtype);
      setImportance(caseData.importance);
      setDescription(caseData.description);
      setMemberNotes(caseData.member_notes);
    }
  }, [caseData]);

  const loadConstituents = async (userId) => {
    setLoadingConstituents(true);

    const res = isCouncillor ? await getCouncillorConstituents(config, userId) : await getConstituents(config, userId);

    if (res.status === 200) {
      setConstituents(res.data.constituents);
    } else {
      console.log('ERROR: Grabbing Constituents');
    }

    setLoadingConstituents(false);
  };

  useEffect(() => {
    if (config.api && userInfo && isOpen) {
      loadConstituents(userInfo.id);
    }
  }, [config, userInfo, isOpen]);

  const checkIsValid = () => {
    if (((!createMode && selectedConstituent) || (createMode && name && postalCode)) && source && type) {
      return true;
    }

    return false;
  };

  const getPayload = () => {
    const constituentData = selectedConstituent
      ? {
          constituent_id: selectedConstituent.id,
          name: selectedConstituent.name,
          phone: selectedConstituent.phone,
          email: selectedConstituent.email,
          address: selectedConstituent.address,
          postal_code: selectedConstituent.postal_code,
        }
      : {
          constituent_id: null,
          name,
          phone,
          email,
          address,
          postal_code: postalCode.toUpperCase().trim(),
        };

    return {
      ...constituentData,
      type,
      subtype: type === 'Social Services' ? subtype : '',
      importance,
      description,
      member_notes: memberNotes,
      source,
    };
  };

  const handleSuccess = (message) => {
    dispatch(
      setNotify({
        status: true,
        type: 'success',
        title: 'Success',
        message,
      }),
    );

    handleGetCaseData();
    onCloseHandler();
  };

  const showError = (message) => {
    dispatch(
      setNotify({
        status: true,
        type: 'error',
        title: 'Error',
        message,
      }),
    );
  };

  const handleSave = async () => {
    if (!checkIsValid()) {
      return showError('Fill in all required fields');
    }

    const payload = getPayload();

    if (!checkPostalCode(payload.postal_code)) {
      return showError(
        'Invalid postal code format. Please enter a 6-character code in the format C#C#C# (C - letter, # - digit)',
      );
    }

    setSaving(true);

    const res = await saveCase(
      config,
      caseData.id,
      userInfo.id,
      payload.constituent_id,
      payload.name,
      payload.phone,
      payload.email,
      payload.address,
      payload.postal_code,
      payload.type,
      payload.subtype,
      payload.importance,
      payload.description,
      payload.member_notes,
      payload.source,
    );

    if (res.status === 200) {
      handleSuccess(res.data.message);
    } else {
      dispatch(
        setNotify({
          status: true,
          type: 'error',
          title: 'Error',
          message: res.data.message,
        }),
      );
    }
    setSaving(false);
  };

  const handleCreate = async () => {
    if (!checkIsValid()) {
      return showError('Fill in all required fields');
    }

    const payload = getPayload();

    if (!checkPostalCode(payload.postal_code)) {
      return showError(
        'Invalid postal code format. Please enter a 6-character code in the format C#C#C# (C - letter, # - digit)',
      );
    }

    setSaving(true);

    const res = await createCase(
      config,
      userInfo.id,
      payload.constituent_id,
      payload.name,
      payload.phone,
      payload.email,
      payload.address,
      payload.postal_code,
      payload.type,
      payload.subtype,
      payload.importance,
      payload.description,
      payload.member_notes,
      payload.source,
    );

    if (res.status === 201) {
      handleSuccess(res.data.message);
    } else {
      dispatch(
        setNotify({
          status: true,
          type: 'error',
          title: 'Error',
          message: res.data.message,
        }),
      );
    }
    setSaving(false);
  };

  useOutsideClick(windowRef, null, isOpen, onCloseHandler);

  return ReactDOM.createPortal(
    <div className="login-wrap feedback">
      <div className={`login-view ${saving ? 'saving' : ''}`}>
        <div ref={windowRef} className="login-box feedback__box">
          {!saving ? (
            <div className="feedback__title">{caseData ? 'Edit Case' : 'Create Case'}</div>
          ) : (
            <>
              <div className="info__title">{caseData ? 'Saving Case' : 'Creating Case'}</div>
              <div className="info__text">The process should take no longer than 30 seconds. Please Wait...</div>
            </>
          )}
          {!saving ? (
            <>
              <div className="feedback__wrapper">
                <PerfectScrollbar
                  options={{
                    wheelPropagation: false,
                    autoHide: false,
                  }}
                >
                  <div className="feedback__form">
                    <div className="feedback__section">
                      <div className="feedback__section-title">Constituent Information</div>
                      {!selectedConstituent && !createMode && (
                        <>
                          <ConstituentSelect
                            label="Constituent *"
                            constituents={constituents}
                            loading={loadingConstituents}
                            setSelectedConstituent={setSelectedConstituent}
                          />

                          <div className="feedback__form-btn" onClick={() => setCreateMode(true)}>
                            + Create new constituent
                          </div>
                        </>
                      )}
                      {selectedConstituent && (
                        <div className="feedback__selected">
                          <div>Name: {checkForEmptyString(selectedConstituent.name)}</div>
                          <div>Email: {checkForEmptyString(selectedConstituent.email)}</div>
                          <div>Phone: {checkForEmptyString(selectedConstituent.phone)}</div>
                          <div>Address: {checkForEmptyString(selectedConstituent.address)}</div>
                          <div>Postal Code: {checkForEmptyString(selectedConstituent.postal_code)}</div>

                          <div className="feedback__form-btn" onClick={() => setSelectedConstituent(null)}>
                            Change
                          </div>
                        </div>
                      )}
                      {createMode && (
                        <>
                          <InputItem
                            label="Name *"
                            type="text"
                            onChange={(e) => setName(e.target.value)}
                            value={name}
                          />
                          <InputItem
                            label="Phone"
                            type="text"
                            onChange={(e) => setPhone(e.target.value)}
                            value={phone}
                          />
                          <div className="feedback__hint">Multiple values should be separated with a semicolon (;)</div>
                          <InputItem
                            label="Email"
                            type="email"
                            onChange={(e) => setEmail(e.target.value)}
                            value={email}
                          />
                          <div className="feedback__hint">Multiple values should be separated with a semicolon (;)</div>
                          <InputItem
                            label="Address"
                            type="text"
                            onChange={(e) => setAddress(e.target.value)}
                            value={address}
                          />
                          <InputItem
                            label="Postal Code *"
                            type="text"
                            onChange={(e) => setPostalCode(e.target.value)}
                            value={postalCode}
                          />
                          <div className="feedback__hint">Format: C#C#C# (C - letter, # - digit), e.g. A1A1A1</div>
                          <div className="feedback__form-btn" onClick={() => setCreateMode(false)}>
                            Select from existing
                          </div>
                        </>
                      )}
                    </div>
                    <div className="feedback__section">
                      <div className="feedback__section-title">Email Information</div>
                      <CustomSelect
                        label="Source *"
                        options={CASE_SOURCE_TYPES}
                        selectedOption={source}
                        setSelectedOption={setSource}
                      />
                      <CustomSelect
                        label="Type *"
                        options={FEEDBACK_TYPES}
                        selectedOption={type}
                        setSelectedOption={setType}
                      />
                      {type === 'Social Services' && (
                        <CustomSelect
                          label="Subtype"
                          options={SOCIAL_SERVICES_SUBTYPES}
                          selectedOption={subtype}
                          setSelectedOption={setSubtype}
                        />
                      )}
                      <CustomSelect
                        label="Importance"
                        options={IMPORTANCE_TYPES}
                        selectedOption={importance}
                        setSelectedOption={setImportance}
                      />
                      <InputItem
                        label="Description"
                        onChange={(e) => setDescription(e.target.value)}
                        value={description}
                        isText={true}
                      />
                      {userInfo && userInfo.role === mppRoleValue && (
                        <InputItem
                          label="Member notes"
                          onChange={(e) => setMemberNotes(e.target.value)}
                          value={memberNotes}
                          isText={true}
                          className="notes"
                        />
                      )}
                    </div>
                  </div>
                </PerfectScrollbar>
              </div>
              <div className="submit">
                <FormButton
                  subLink={'Cancel'}
                  subLinkClick={onCloseHandler}
                  text={caseData ? 'Save' : 'Submit'}
                  onClick={caseData ? handleSave : handleCreate}
                />
              </div>
            </>
          ) : (
            <Fader size={50} />
          )}
        </div>
      </div>
    </div>,
    document.getElementById('root'),
  );
};

export default CaseForm;
